import React, {Component} from 'react';
import './styles.scss';
import CardHelper from '../../helpers';
import Asset from 'src/requests/apa/Asset';
import { getCoverStyle, isPS4Platform, clearCoverRef } from '../ps4ImageUtils';

// Detectar PS4 una sola vez a nivel de módulo
const _isPS4 = isPS4Platform();

class CardView extends Component {
  constructor(props) {
    super(props);
    this.state = { imageReady: !_isPS4 };
    this._coverRef = null;
    this._loadTimer = null;

    // Binding estable en constructor — evita crear nuevas funciones en cada render
    this._setCoverRef = this._setCoverRef.bind(this);

    // Cache de elementos JSX para evitar recrearlos en cada render
    // Solo se recalculan cuando sus props específicos cambian
    this._cachedProgressBar = null;
    this._cachedBadge = null;
    this._cachedTitle = null;
    this._cachedFooter = null;

    // Cache de valores previos de props para detectar cambios
    this._prevBookmark = undefined;
    this._prevBadges = undefined;
    this._prevTitle = undefined;
    this._prevProvider = undefined;
    this._prevLiveEnabled = undefined;
    this._prevIsRecentActivity = undefined;
    this._prevShowActions = undefined;
    this._prevCover = undefined;
    this._prevClassOutLineFocus = undefined;
    this._prevImageReady = undefined;

    // Cache de valores computados del render
    this._cachedCoverClassName = '';
    this._cachedCoverStyle = null;
    this.showActions = props.showActions;
    this.cover = props.cover;
    this.bookmark = props.bookmark;
    this.badges = props.badges;
    this.showTitles = props.showTitles;
    this.title = props.title;
    this.isRecentActivity = props.isRecentActivity;
    this.onDeleteAction = props.onDeleteAction;
    this.data = props.data;
    this.id = props.id;
    this.isChannelBlocked = props.isChannelBlocked;
  }

  componentDidMount() {
    if (!this.state.imageReady) {
      // Cargar imagen después de que el DOM se estabilice
      this._loadTimer = setTimeout(this._onImageReady, _isPS4 ? 200 : 150);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.cover !== nextProps.cover ||
      this.props.bookmark !== nextProps.bookmark ||
      this.props.badges !== nextProps.badges ||
      this.props.showActions !== nextProps.showActions ||
      this.props.isRecentActivity !== nextProps.isRecentActivity ||
      this.props.title !== nextProps.title ||
      this.props.classOutLineFocus !== nextProps.classOutLineFocus ||
      this.state.imageReady !== nextState.imageReady
    );
  }

  componentWillUnmount() {
    if (this._loadTimer) {
      clearTimeout(this._loadTimer);
      this._loadTimer = null;
    }
    // Forzar limpieza de imagen para liberar memoria en PS4
    clearCoverRef(this._coverRef);
    this._coverRef = null;

    // Nulificar caches para liberar memoria
    this._cachedProgressBar = null;
    this._cachedBadge = null;
    this._cachedTitle = null;
    this._cachedFooter = null;
    this._cachedCoverStyle = null;
  }

  _onImageReady = function() {
    this.setState({ imageReady: true });
  }.bind(this);

  _setCoverRef(el) {
    this._coverRef = el;
  }

  _updateCachedElements() {
    const props = this.props;
    const bookmark = props.bookmark;
    const badges = props.badges;
    const title = props.title;
    const isRecentActivity = props.isRecentActivity;
    const showActions = props.showActions;
    const cover = props.cover;
    const classOutLineFocus = props.classOutLineFocus;
    const data = props.data;
    const imageReady = this.state.imageReady;

    const provider = (data && data.provider) || '';
    const liveEnabled = (data && data.live_enabled) || '0';

    // Actualizar progress bar solo si bookmark cambió
    if (bookmark !== this._prevBookmark) {
      this._prevBookmark = bookmark;
      this._cachedProgressBar = CardHelper.createProgressBar(bookmark);
    }

    // Actualizar badge solo si badges cambió
    if (badges !== this._prevBadges) {
      this._prevBadges = badges;
      this._cachedBadge = CardHelper.createBadge(badges);
    }

    // Actualizar title solo si title, provider o liveEnabled cambiaron
    if (title !== this._prevTitle || provider !== this._prevProvider || liveEnabled !== this._prevLiveEnabled) {
      this._prevTitle = title;
      this._prevProvider = provider;
      this._prevLiveEnabled = liveEnabled;
      const isTitleEnabled = CardHelper.posterTitleIsEnabled(provider);
      const isChannel = liveEnabled === '1';
      this._cachedTitle = CardHelper.createTitle(title, isTitleEnabled, isChannel);
    }

    // Actualizar footer solo si isRecentActivity o showActions cambiaron
    if (isRecentActivity !== this._prevIsRecentActivity || showActions !== this._prevShowActions) {
      this._prevIsRecentActivity = isRecentActivity;
      this._prevShowActions = showActions;
      this._cachedFooter = CardHelper.createFooter(isRecentActivity, showActions);
    }

    // Actualizar cover className y style solo si cambiaron
    if (classOutLineFocus !== this._prevClassOutLineFocus) {
      this._prevClassOutLineFocus = classOutLineFocus;
      this._cachedCoverClassName = 'card-cover ' + (classOutLineFocus || '');
    }

    if (cover !== this._prevCover || imageReady !== this._prevImageReady) {
      this._prevCover = cover;
      this._prevImageReady = imageReady;
      this._cachedCoverStyle = getCoverStyle(cover, imageReady);
    }
  }

  render() {
    // Actualizar solo los elementos cuyos props cambiaron
    this._updateCachedElements();

    const cover = this.props.cover;

    const iconBlocked = Asset.get('Metadata_ContenidoBloqueado_IconoBloqueado');
    const imageWithTransparency = Asset.get('Metadata_ContenidoBloqueado_Transparencia');
    const existsiconBlocked = iconBlocked !== 'Metadata_ContenidoBloqueado_IconoBloqueado';
    const existsImageWithTransparency = imageWithTransparency !== 'Metadata_ContenidoBloqueado_Transparencia';

    return (
      <div className={`card card-view`}>
        <div className={'card-container'}>
           {
            this.isChannelBlocked && existsiconBlocked &&
            (
              <div>
                <div
                  className='card-cover-overlay'
                  style={{ backgroundImage: `url(${this.isChannelBlocked && existsImageWithTransparency ? imageWithTransparency : null})` }}
                />
                <img className='icon-channel-blocked'
                  src={iconBlocked}
                />
              </div>
            )
          }
          <div className={'preview-container'}>
            {this.cover !== '' &&
            <div
              ref={this._setCoverRef}
              className={this._cachedCoverClassName}
              style={this._cachedCoverStyle}
            />
            }
            {this._cachedProgressBar}
            {this._cachedBadge}
          </div>
          {this._cachedTitle}
        </div>
        <div className='card-labels-container'>
          {this._cachedFooter}
        </div>
      </div>
    );
  }
}

export default CardView;



// WEBPACK FOOTER //
// ./src/components/CardNew/cardTypes/CardView/index.js